/** Consolidated ReportsView utilities. */

import { useDraggable } from "@dnd-kit/core";
import _ from "lodash";
import type { ReactNode } from "react";
import { GripVerticalIcon, type LucideIcon } from "lucide-react";

import {
  ChartFormatEnum,
  FrequencyEnum,
  MetricModeEnum,
} from "@/__generated__/graphql";
import type { ChartData } from "@/__generated__/graphql";
import type { FilterDefinitionType, FilterType } from "@/components/@types";
import { normalizeFilterOperator } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.utils";
import { objectForReportSource } from "@/objects";
import { Utils } from "@/utils";
import type {
  AppViewTabModeType,
  BreakpointKey,
  DashboardProfile,
} from "@/state/@types";
import { Badge } from "@/components/ui/badge";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import {
  canonicalizeReportLayouts,
  canonicalizeStackReportLayouts,
  ensureGridPlacement,
  fitCardsToGrid,
  isDesktopLayoutBreakpoint,
  mergeCardGridPlacement,
  REPORTS_DESKTOP_COLUMNS,
  resolveDesktopLayoutCards,
} from "./reportsGridLayout";

export { fitCardsToGrid };

// ── Navigation ──────────────────────────────────────────────────────────

/** Storage key for views when the app view has no tabs (``activeTab`` is null). */
const REPORTS_TAB_KEY = "default" as const;

export function getReportsTabKey(
  activeTab: AppViewTabModeType | null | undefined,
): string {
  return activeTab ?? REPORTS_TAB_KEY;
}

export function getSelectedViewId(
  activeTabNodeIds: Record<string, unknown> | undefined,
  activeTab: AppViewTabModeType | null | undefined,
  routeViewId?: string | null,
): string | null {
  const tabIds = activeTabNodeIds as {
    reports?: Record<string, string | null>;
    views?: Record<string, string | null>;
  };
  const reportsMap = tabIds?.reports ?? tabIds?.views;
  return reportsMap?.[getReportsTabKey(activeTab)] ?? routeViewId ?? null;
}

// ── Dashboard card registry ─────────────────────────────────────────────

type DashboardCardDef = {
  id: string;
  label: string;
  defaultColSpan: number;
  minColSpan: number;
  maxColSpan: number;
};

// Four cards, each half of the four-column page, so a new report opens as two
// even rows. Mirrors ``DEFAULT_WIDGET_CATALOG`` in the backend, which is what
// a report is actually built from.
const DASHBOARD_CARDS: DashboardCardDef[] = [
  {
    id: "overview-kpis",
    label: "Overview KPIs",
    defaultColSpan: 2,
    minColSpan: 1,
    maxColSpan: 4,
  },
  {
    id: "money-in-out-chart",
    label: "Money In / Out",
    defaultColSpan: 2,
    minColSpan: 2,
    maxColSpan: 4,
  },
  {
    id: "spending-chart",
    label: "Spending by Category",
    defaultColSpan: 2,
    minColSpan: 1,
    maxColSpan: 4,
  },
  {
    id: "bank-accounts",
    label: "Bank Accounts",
    defaultColSpan: 2,
    minColSpan: 2,
    maxColSpan: 4,
  },
];

// ── Filters ─────────────────────────────────────────────────────────────

const REPORTS_FILTER_KEYS = new Set(["dates", "frequency"]);

export function getDefaultReportsFilters(): FilterType[] {
  const now = new Date();
  const year = now.getFullYear();

  return [
    {
      key: "dates",
      type: "date",
      operator: "range",
      value: {
        date1: new Date(year, 0, 1).toISOString(),
        date2: new Date(year, 11, 31).toISOString(),
      },
    },
    {
      key: "frequency",
      type: "enum",
      operator: "exact",
      value: FrequencyEnum.Monthly,
    },
  ] as FilterType[];
}

export function getReportsFilters(filters: FilterType[]): FilterType[] {
  const defaults = getDefaultReportsFilters();
  const nonReportsFilters = filters.filter(
    (filter) => !REPORTS_FILTER_KEYS.has(filter.key),
  );

  const resolvedReportsFilters = defaults.map(
    (defaultFilter) =>
      filters.find((filter) => filter.key === defaultFilter.key) ??
      defaultFilter,
  );

  return [...resolvedReportsFilters, ...nonReportsFilters];
}

export function getReportSubnavFilters(
  filters: FilterType[],
  entityIdentifiers: string[],
): FilterType[] {
  const base = getReportsFilters(filters).filter(
    (filter) => filter.key !== "entities",
  );

  if (entityIdentifiers.length <= 1) {
    return base;
  }

  const entitiesFilter =
    filters.find((filter) => filter.key === "entities") ??
    ({
      key: "entities",
      type: "enum",
      operator: "inList",
      value: entityIdentifiers,
    } as FilterType);

  return [...base, entitiesFilter];
}

export function resolveLayoutEntityIdentifiers(
  layoutEntityIdentifiers: string[],
  filters: FilterType[],
): string[] {
  const published = layoutEntityIdentifiers.map(String);
  const entitiesFilter = filters.find((filter) => filter.key === "entities");
  const selected = Array.isArray(entitiesFilter?.value)
    ? entitiesFilter.value.map(String)
    : [];

  if (!selected.length) {
    return published;
  }

  const subset = selected.filter((identifier) =>
    published.includes(identifier),
  );
  return subset.length ? subset : published;
}

/** Data source — what to fetch. */
/** How a widget is drawn. The server's own list, which it validates against. */
export type PresentationKind =
  | "TREE"
  | "BAR_CHART"
  | "LINE_CHART"
  | "PIE_CHART"
  | "TABLE"
  | "GROUPED_SUMMARY"
  | "KPI_STRIP"
  | "MATRIX";

/**
 * What a widget can be of, as the server publishes it.
 *
 * The browser used to keep its own copy of the fourteen constructs, what each
 * could be drawn as, and which renderer answered for each pair - three tables
 * that had to be kept in step with the backend's own. It is published now, so
 * a source the server grows appears in the picker with nothing changed here.
 */
export type ReportSource = {
  name: string;
  label: string;
  presentations: PresentationKind[];
  filters: string[];
  isSummarized: boolean;
  /** What its rows can be cut by and what they can total. */
  fields: ReportSourceField[];
};

/** One column of a source: something to group a chart by, or to total. */
export type ReportSourceField = {
  name: string;
  label: string;
  kind: string;
  /** What it holds - a reference, a date, money, a number, a choice, words. */
  holds?: string;
  /** The record type a column points at, where it points at one. */
  typename?: string | null;
  isGroupable: boolean;
  isMeasurable: boolean;
  /** A date is cut into periods; nothing else is. */
  takesInterval: boolean;
};

export type PresentationCatalogEntry = {
  id: string;
  kind: PresentationKind;
  label: string;
  description: string;
};

export const PRESENTATION_LABELS: Record<PresentationKind, string> = {
  TREE: "Tree",
  BAR_CHART: "Bar chart",
  LINE_CHART: "Line chart",
  PIE_CHART: "Pie chart",
  TABLE: "Table",
  GROUPED_SUMMARY: "Grouped summary",
  KPI_STRIP: "Metrics",
  MATRIX: "Matrix",
};

export const PRESENTATION_CATALOG: PresentationCatalogEntry[] = [
  {
    id: "presentation-tree",
    kind: "TREE",
    label: "Tree",
    description: "Hierarchical tree view",
  },
  {
    id: "presentation-bar",
    kind: "BAR_CHART",
    label: "Bar chart",
    description: "Vertical or horizontal bars",
  },
  {
    id: "presentation-line",
    kind: "LINE_CHART",
    label: "Line chart",
    description: "Trend lines over time",
  },
  {
    id: "presentation-pie",
    kind: "PIE_CHART",
    label: "Pie chart",
    description: "Categorical breakdown",
  },
  {
    id: "presentation-table",
    kind: "TABLE",
    label: "Table",
    description: "Tabular rows",
  },
  {
    id: "presentation-grouped",
    kind: "GROUPED_SUMMARY",
    label: "Grouped summary",
    description: "Nested metric groups",
  },
  {
    id: "presentation-kpi",
    kind: "KPI_STRIP",
    label: "Metrics",
    description: "Key metric values",
  },
  {
    id: "presentation-matrix",
    kind: "MATRIX",
    label: "Matrix",
    description: "Entity balance matrix",
  },
];

const PRESENTATION_BY_KIND = new Map(
  PRESENTATION_CATALOG.map((entry) => [entry.kind, entry]),
);

/** An old layout still says DATA_LIST; it has been a table for a while. */
export function normalizePresentationKind(kind: string): PresentationKind {
  return (kind === "DATA_LIST" ? "TABLE" : kind) as PresentationKind;
}

export type WidgetPairingStatus =
  | "complete"
  | "needs-presentation"
  | "needs-source";

export function getWidgetPairingStatus(
  widget: ReportLayoutWidget,
): WidgetPairingStatus {
  const hasSource = Boolean(widget.source);
  const hasPresentation = Boolean(widget.presentation);
  if (hasSource && hasPresentation) return "complete";
  if (hasSource) return "needs-presentation";
  // A widget that says nothing at all is not finished either: called complete,
  // it offered nothing to choose and sat on its skeleton.
  return "needs-source";
}

/** Widgets the server-backed definition has all of (post-mutation), by id. */
export function persistedWidgets(
  definition: ReportLayoutDefinition | null | undefined,
): ReadonlyMap<string, ReportLayoutWidget> {
  if (!definition) return new Map();
  return new Map(
    definition.widgets
      .filter((widget) => getWidgetPairingStatus(widget) === "complete")
      .map((widget) => [widget.id, widget]),
  );
}

/** The server refuses a widget that does not say what it is of and how to draw it. */
export function isDefinitionPersistable(
  definition: ReportLayoutDefinition,
): boolean {
  return definition.widgets.every(
    (widget) => getWidgetPairingStatus(widget) === "complete",
  );
}

export function presentationsForSource(
  source: ReportSource | undefined,
): PresentationCatalogEntry[] {
  return (source?.presentations ?? [])
    .map((kind) => PRESENTATION_BY_KIND.get(kind))
    .filter((entry): entry is PresentationCatalogEntry => entry != null);
}

export function sourcesForPresentation(
  presentation: PresentationKind,
  catalogue: readonly ReportSource[],
): ReportSource[] {
  return catalogue.filter((source) =>
    source.presentations.includes(normalizePresentationKind(presentation)),
  );
}

export function sourceByName(
  name: string | null | undefined,
  catalogue: readonly ReportSource[],
): ReportSource | undefined {
  if (!name) return undefined;
  return catalogue.find((source) => source.name === name);
}

/** Strip deprecated fields and say a presentation by the name it means now. */
export function normalizeWidget(
  widget: ReportLayoutWidget,
): ReportLayoutWidget {
  const rest = { ...widget } as ReportLayoutWidget & {
    type?: unknown;
    config?: unknown;
    construct?: unknown;
  };
  delete rest.type;
  delete rest.config;
  delete rest.construct;
  if (!rest.presentation) return rest;
  return {
    ...rest,
    presentation: normalizePresentationKind(rest.presentation),
  };
}

/**
 * What a widget is called.
 *
 * Its own title where it was given one, and otherwise the question it asks -
 * "total transaction amount per category" rather than the bare "Transactions",
 * which is the same word on every widget cut from the same rows. A source that
 * works out its own figure has no question, and ``sentenceFor`` gives back its
 * label.
 */
export function widgetDisplayTitle(
  widget: ReportLayoutWidget,
  catalogue: readonly ReportSource[] = [],
): string {
  const title = widget.title?.trim();
  if (title) return title;
  const source = sourceByName(widget.source, catalogue);
  return (
    sentenceFor(questionFromSpec(widget.spec), source) ||
    source?.label ||
    "Widget"
  );
}

/**
 * The question a chart asks: what to cut it by, and what to measure.
 *
 * Up to two cuts. One is an axis - spending by month - and two is an axis and
 * a series drawn at each point: spending by month, by category. The server
 * reads the same shape whether the agent wrote it or somebody chose it here.
 */
export type WidgetQuestion = {
  groupBy: string[];
  operation: SummaryOperation;
  /** What is being totalled; counting rows needs no field. */
  field?: string;
  /** Which rows the question is asked of, when it is not asked of all of them. */
  where?: WhereGroup | null;
};

/** One comparison in a where clause, as the server stores it. */
export type WhereRule = {
  field: string;
  operator: string;
  value?: unknown;
  isNegated?: boolean;
};

/**
 * Comparisons taken together, and the groups of them inside those.
 *
 * The same tree the server reads: ``all`` of its terms or ``any`` of them,
 * either read the other way round when it is negated, and a term is a
 * comparison or another group - which is what makes "at Shell, and either
 * over a hundred or fuel" a thing a chart can be asked.
 */
export type WhereGroup = {
  connector: "all" | "any";
  isNegated?: boolean;
  terms: Array<WhereRule | WhereGroup>;
};

export function isWhereGroup(term: WhereRule | WhereGroup): term is WhereGroup {
  return Array.isArray((term as WhereGroup).terms);
}

/**
 * Whether a widget can be narrowed at all.
 *
 * A source that is worked out rather than grouped - a cash flow, a balance
 * sheet, the bank accounts as they stand - has no rows to compare, so there
 * is nothing to say "where" about. One answer, so the sentence and the clause
 * cannot disagree about whether there is a clause: the punctuation before it
 * is written by the sentence, and a comma with nothing after it is the kind
 * of thing that ends up on screen.
 */
export function canBeNarrowed(source: ReportSource | undefined): boolean {
  return Boolean(source?.isSummarized) && (source?.fields ?? []).length > 0;
}

/**
 * A clause with its half-written comparisons left out.
 *
 * A row is written a piece at a time - the field, then how it compares, then
 * what to - and a comparison with nothing to compare against is not a
 * question, it is a question being typed. Asked anyway, it would narrow the
 * widget to nothing between one keystroke and the next.
 */
export function whereAsked(where: WhereGroup): WhereGroup {
  const needsNoValue = (rule: WhereRule) => rule.operator === "is_null";
  return {
    ...where,
    terms: (where.terms ?? [])
      .map((term) => (isWhereGroup(term) ? whereAsked(term) : term))
      .filter((term) =>
        isWhereGroup(term)
          ? term.terms.length > 0
          : Boolean(term.field) &&
            (needsNoValue(term) ||
              (term.value !== undefined &&
                term.value !== null &&
                term.value !== "")),
      ),
  };
}

/** Whether a clause narrows anything, once the empty groups are seen through. */
export function whereAsks(where: WhereGroup | null | undefined): boolean {
  return (where?.terms ?? []).some((term) =>
    isWhereGroup(term) ? whereAsks(term) : Boolean(term.field),
  );
}

export type SummaryOperation = "sum" | "count" | "avg" | "min" | "max";

export const SUMMARY_OPERATION_LABELS: Record<SummaryOperation, string> = {
  sum: "Total",
  count: "Number of",
  avg: "Average",
  min: "Smallest",
  max: "Largest",
};

/** A date is cut into months unless the viewer asks for another period. */
const DEFAULT_INTERVAL = "MONTH";

/** The most levels a chart is cut by. One, for now: an axis and nothing else. */
export const MAX_GROUPING_DEPTH = 1;

export function groupableFields(
  source: ReportSource | undefined,
): ReportSourceField[] {
  return (source?.fields ?? []).filter((field) => field.isGroupable);
}

export function measurableFields(
  source: ReportSource | undefined,
): ReportSourceField[] {
  return (source?.fields ?? []).filter((field) => field.isMeasurable);
}

/** The question a stored spec is asking. */
export function questionFromSpec(
  spec: Record<string, unknown> | undefined,
): WidgetQuestion {
  const groups = Array.isArray(spec?.groupBy)
    ? (spec?.groupBy as Array<Record<string, unknown>>)
    : [];
  const aggregate = Array.isArray(spec?.aggregate)
    ? (spec?.aggregate as Array<Record<string, unknown>>)
    : [];
  const measure = aggregate[0] ?? {};
  return {
    groupBy: groups
      .map((group) => String(group.field ?? ""))
      .filter(Boolean)
      .slice(0, MAX_GROUPING_DEPTH),
    operation: (String(measure.op ?? "count") as SummaryOperation) ?? "count",
    field: measure.field ? String(measure.field) : undefined,
    where: (spec?.where as WhereGroup | undefined) ?? null,
  };
}

/**
 * The spec that asks *question* of *source*, keeping everything else it says.
 *
 * A date is grouped by period rather than by the instant it happened, and the
 * period itself is the viewer's to choose - the server rewrites it from the
 * frequency the report is being read at.
 */
export function specForQuestion(
  spec: Record<string, unknown> | undefined,
  question: WidgetQuestion,
  source: ReportSource | undefined,
): Record<string, unknown> {
  const fields = new Map(
    (source?.fields ?? []).map((field) => [field.name, field]),
  );
  return {
    ...(spec ?? {}),
    groupBy: question.groupBy.filter(Boolean).map((name) => ({
      field: name,
      ...(fields.get(name)?.takesInterval
        ? { interval: DEFAULT_INTERVAL }
        : {}),
    })),
    aggregate: [
      question.operation === "count"
        ? { op: "count" }
        : {
            op: question.operation,
            field: question.field ?? measurableFields(source)[0]?.name,
          },
    ],
    // A clause that narrows nothing is not stored: an empty group would have
    // the widget asking the server about a question it is not asking.
    where: whereAsks(question.where) ? question.where : undefined,
  };
}

/**
 * What a way of drawing can say, which is not everything.
 *
 * A line joins its points, and joining them claims one follows the next: over
 * months that is a trend, over categories it is a line between two things
 * that have no order at all. A pie is the parts of one whole, so its slices
 * have to be kinds of thing rather than months - and it has one ring, so
 * there is nowhere to put a second cut.
 */
export type DrawingRule = {
  /** What the axis has to be for the drawing to mean anything. */
  axis: "date" | "category" | "any";
  /**
   * Whether the drawing adds its parts up. A pie's slices and a stacked
   * area's bands only mean something if they sum to the whole, and averages
   * do not sum - the average of two averages is not the average.
   */
  addsUp: boolean;
};

export const DRAWING_RULES: Record<PresentationKind, DrawingRule> = {
  BAR_CHART: { axis: "any", addsUp: false },
  LINE_CHART: { axis: "date", addsUp: false },
  PIE_CHART: { axis: "category", addsUp: true },
  TABLE: { axis: "any", addsUp: false },
  GROUPED_SUMMARY: { axis: "any", addsUp: false },
  KPI_STRIP: { axis: "any", addsUp: false },
  TREE: { axis: "any", addsUp: false },
  MATRIX: { axis: "any", addsUp: false },
};

/** The ways of drawing a widget offers, as one list with one name each. */
export const DRAWINGS: PresentationKind[] = [
  "BAR_CHART",
  "LINE_CHART",
  "PIE_CHART",
  "GROUPED_SUMMARY",
];

/** Operations whose parts add up to the whole. */
export const ADDITIVE_OPERATIONS: SummaryOperation[] = ["sum", "count"];

export function isAdditive(operation: SummaryOperation): boolean {
  return ADDITIVE_OPERATIONS.includes(operation);
}

export function drawingRuleFor(
  presentation: PresentationKind | undefined,
): DrawingRule {
  return (
    DRAWING_RULES[normalizePresentationKind(presentation ?? "BAR_CHART")] ??
    DRAWING_RULES.BAR_CHART
  );
}

/** The fields this drawing can put along its axis. */
export function axisFieldsFor(
  source: ReportSource | undefined,
  presentation: PresentationKind | undefined,
): ReportSourceField[] {
  const rule = drawingRuleFor(presentation);
  return groupableFields(source).filter(
    (field) =>
      rule.axis === "any" ||
      (rule.axis === "date" ? field.takesInterval : !field.takesInterval),
  );
}

/**
 * The same question, cut a way this drawing can draw.
 *
 * Asked for a line chart of spending by category, the honest answer is not to
 * refuse it but to draw the thing that was meant: the same total over time.
 * So the axis moves to what the drawing needs and the rest of the question -
 * what is being measured - is left alone.
 */
export function questionForPresentation(
  question: WidgetQuestion,
  presentation: PresentationKind | undefined,
  source: ReportSource | undefined,
): WidgetQuestion {
  const rule = drawingRuleFor(presentation);
  const allowed = axisFieldsFor(source, presentation);
  const axis = allowed.some((field) => field.name === question.groupBy[0])
    ? question.groupBy[0]
    : allowed[0]?.name;
  return { ...question, groupBy: [axis].filter(Boolean) };
}

/**
 * A field as it reads in the middle of a sentence.
 *
 * The column is called ``category_obj__name`` and labelled "Category"; in a
 * sentence it is "category", because a sentence is not a heading.
 */
export function saidAs(field: ReportSourceField | undefined): string {
  return (field?.label ?? "").toLowerCase();
}

/** A source as it reads as the thing being counted: "transactions". */
export function saidAsRows(source: ReportSource | undefined): string {
  return (source?.label ?? "rows").toLowerCase();
}

/**
 * What a widget is asking, in English.
 *
 * This is the card's title as well as its editor: "total transaction amount
 * by category" is both what it is called and what it does, so nobody has to
 * name a chart before they can look at it. A source that works its own figure
 * out has no question, and is called what it is - "Cash flow".
 */
export function sentenceFor(
  question: WidgetQuestion,
  source: ReportSource | undefined,
): string {
  if (!source) return "";
  if (!source.isSummarized) return source.label;

  const fields = new Map(
    (source.fields ?? []).map((field) => [field.name, field]),
  );
  const rows = saidAsRows(source);
  const measured = saidAs(fields.get(question.field ?? ""));
  // "How many transactions", but "Total transaction amount": counting takes
  // the rows themselves, and everything else takes one of their columns.
  const measure =
    question.operation === "count"
      ? `${SUMMARY_OPERATION_LABELS.count} ${rows}`
      : `${SUMMARY_OPERATION_LABELS[question.operation]} ${rows.replace(/s$/, "")} ${measured}`.trim();

  const along = saidAs(fields.get(question.groupBy[0] ?? ""));
  const asked = along ? `${measure} per ${along}` : `${measure} in total`;
  const narrowed = clauseAsSaid(question.where, source);
  return narrowed ? `${asked}, where ${narrowed}` : asked;
}

/**
 * A clause as a person reads it: "merchant is Shell and amount is over 100".
 *
 * A nested group is said in brackets, because "a and b or c" means two
 * different things depending on where the brackets were, and the chart is
 * drawn from one of them.
 */
export function clauseAsSaid(
  where: WhereGroup | null | undefined,
  source: ReportSource | undefined,
): string {
  if (!whereAsks(where)) return "";
  const fields = new Map(
    (source?.fields ?? []).map((field) => [field.name, field]),
  );
  const joiner = where!.connector === "any" ? " or " : " and ";
  const said = (where!.terms ?? [])
    .map((term) => {
      if (isWhereGroup(term)) {
        const inner = clauseAsSaid(term, source);
        // Brackets are for where they change the meaning: a group of one
        // reads as the comparison it holds.
        if (!inner) return "";
        return term.terms.filter((one) =>
          isWhereGroup(one) ? whereAsks(one) : one.field,
        ).length > 1
          ? `(${inner})`
          : inner;
      }
      if (!term.field) return "";
      const field = fields.get(term.field);
      const name = field?.label ?? saidAs(field) ?? term.field;
      const comparison = term.isNegated
        ? (WHERE_OPERATOR_LABELS_NEGATED[term.operator] ??
          `is not ${WHERE_OPERATOR_LABELS[term.operator] ?? term.operator}`)
        : (WHERE_OPERATOR_LABELS[term.operator] ?? term.operator);
      const value = valueAsSaid(term.value, field, { quoted: true });
      return `${name.toLowerCase()} ${comparison}${value ? ` ${value}` : ""}`.trim();
    })
    .filter(Boolean)
    .join(joiner);
  return where!.isNegated && said ? `not (${said})` : said;
}

/**
 * What a comparison is compared against, written as the column is read.
 *
 * Money is money on the page - ten thousand is $10,000, not 10000 - and a
 * figure that is not money still carries the separators that make it a number
 * at a glance rather than a string of digits to count.
 */
export function valueAsSaid(
  value: unknown,
  field: ReportSourceField | undefined,
  /**
   * Said in a sentence rather than shown in the box it is typed into: words
   * go in quotes there, so "merchant is "Shell"" cannot be read as the
   * sentence running on into something else. A figure and a date need no
   * quotes - nothing else could be mistaken for them.
   */
  options?: { quoted?: boolean },
): string {
  if (value === undefined || value === null || value === "") return "";
  if (Array.isArray(value)) {
    return value
      .map((one) => valueAsSaid(one, field, options))
      .filter(Boolean)
      .join(", ");
  }
  const asNumber = typeof value === "number" ? value : Number(value);
  const isWords = field?.kind !== "money" && field?.kind !== "date";
  if (Number.isNaN(asNumber) || typeof value === "boolean") {
    return options?.quoted && isWords ? `"${String(value)}"` : String(value);
  }
  if (field?.kind === "money")
    return Utils.fCurrency(asNumber, false, !Number.isInteger(asNumber));
  return asNumber.toLocaleString();
}

/** How each comparison reads in a sentence. */
export const WHERE_OPERATOR_LABELS: Record<string, string> = {
  eq: "is",
  contains: "contains",
  starts_with: "starts with",
  ends_with: "ends with",
  gt: "is over",
  gte: "is at least",
  lt: "is under",
  lte: "is at most",
  in: "is one of",
  is_null: "is empty",
};

/** And how each reads when it is turned around, which is not "is not is". */
export const WHERE_OPERATOR_LABELS_NEGATED: Record<string, string> = {
  eq: "is not",
  contains: "does not contain",
  starts_with: "does not start with",
  ends_with: "does not end with",
  gt: "is not over",
  gte: "is under",
  lt: "is not under",
  lte: "is over",
  in: "is none of",
  is_null: "is not empty",
};

/**
 * Why a drawing cannot answer a question as it stands, in plain words.
 *
 * Said where the drawing is offered, so choosing it is an informed choice
 * rather than a surprise. Nothing is hidden and nothing changes silently:
 * every way of drawing stays on the row, and the ones that would have to
 * change the question say what they would change.
 */
export function whyItCannotDraw(
  presentation: PresentationKind,
  question: WidgetQuestion,
  source: ReportSource | undefined,
): string | null {
  const rule = drawingRuleFor(presentation);
  const named = PRESENTATION_LABELS[presentation].toLowerCase();
  const fields = new Map(
    (source?.fields ?? []).map((field) => [field.name, field]),
  );
  const axis = fields.get(question.groupBy[0] ?? "");

  if (rule.addsUp && !isAdditive(question.operation)) {
    const said = SUMMARY_OPERATION_LABELS[question.operation].toLowerCase();
    return `A ${named} adds its parts up, and ${said} figures do not add up.`;
  }
  if (axis && rule.axis === "date" && !axis.takesInterval) {
    return `A ${named} joins its points, so it needs a date along the bottom.`;
  }
  if (axis && rule.axis === "category" && axis.takesInterval) {
    return `A ${named} is the parts of one whole, so it needs kinds of thing rather than dates.`;
  }
  return null;
}

/**
 * The question, changed as little as it takes for *presentation* to draw it.
 *
 * Asked for a pie of the average spend by month, the useful answer is neither
 * to refuse nor to draw something else entirely: it is the total by category,
 * changed one piece at a time, with each change said out loud.
 */
export function adaptQuestion(
  question: WidgetQuestion,
  presentation: PresentationKind,
  source: ReportSource | undefined,
): { question: WidgetQuestion; changed: string[] } {
  const rule = drawingRuleFor(presentation);
  const fields = new Map(
    (source?.fields ?? []).map((field) => [field.name, field]),
  );
  const changed: string[] = [];
  let adapted = { ...question };

  if (rule.addsUp && !isAdditive(adapted.operation)) {
    const was = SUMMARY_OPERATION_LABELS[adapted.operation].toLowerCase();
    adapted = { ...adapted, operation: adapted.field ? "sum" : "count" };
    changed.push(
      `${was} became ${SUMMARY_OPERATION_LABELS[adapted.operation].toLowerCase()}`,
    );
  }

  const allowed = axisFieldsFor(source, presentation);
  if (
    adapted.groupBy[0] &&
    !allowed.some((field) => field.name === adapted.groupBy[0])
  ) {
    const was = saidAs(fields.get(adapted.groupBy[0]));
    const now = allowed[0];
    adapted = {
      ...adapted,
      groupBy: [now?.name, ...adapted.groupBy.slice(1)].filter(Boolean),
    };
    if (now) changed.push(`per ${was} became per ${saidAs(now)}`);
  }

  // Cut the same way twice says nothing twice.
  const cuts = adapted.groupBy.filter(
    (name, index) => adapted.groupBy.indexOf(name) === index,
  );
  return {
    question: { ...adapted, groupBy: cuts.slice(0, MAX_GROUPING_DEPTH) },
    changed,
  };
}

/**
 * What a source is worth showing before anyone has said what they want.
 *
 * A widget dropped on the canvas has to draw something straight away, so it
 * asks the question its rows most obviously answer - what happened over time,
 * totalled by money if it has any, counted if it has none - cut a way the
 * drawing it arrived as can actually draw.
 */
export function defaultQuestionFor(
  source: ReportSource,
  presentation?: PresentationKind,
): WidgetQuestion {
  const dates = groupableFields(source).filter((field) => field.takesInterval);
  const preferred = ["effective_date", "date_issued", "date", "created_at"];
  const when =
    preferred
      .map((name) => dates.find((field) => field.name === name))
      .find(Boolean) ?? dates[0];
  const amounts = measurableFields(source);
  const money =
    ["amount", "amount_total", "balance"]
      .map((name) => amounts.find((field) => field.name === name))
      .find(Boolean) ?? amounts.find((field) => field.kind === "money");

  const asked: WidgetQuestion = {
    groupBy: when
      ? [when.name]
      : groupableFields(source)
          .slice(0, 1)
          .map((field) => field.name),
    operation: money ? "sum" : "count",
    field: money?.name,
  };
  return questionForPresentation(
    asked,
    presentation ?? source.presentations[0],
    source,
  );
}

/**
 * A widget with one part of its question changed.
 *
 * Changing the subject changes what can be asked about it - documents have no
 * merchant and transactions have no due date - so the rest of the question is
 * asked afresh of the new rows rather than left pointing at columns that are
 * not there.
 */
export function askedOfWidget(
  widget: ReportLayoutWidget,
  patch: {
    spec?: Record<string, unknown>;
    presentation?: PresentationKind;
    source?: string;
  },
  catalogue: readonly ReportSource[],
): Partial<ReportLayoutWidget> {
  const changed: Partial<ReportLayoutWidget> =
    patch.source && patch.source !== widget.source
      ? askedAfresh(widget, sourceByName(patch.source, catalogue))
      : patch;
  const next = { ...widget, ...changed };
  const source = sourceByName(next.source, catalogue);
  // The sentence is the widget's name wherever it is read - a shared link has
  // no picker to build it from, so it is written down as it is chosen.
  return {
    ...changed,
    title: sentenceFor(questionFromSpec(next.spec), source),
  };
}

/** The same widget asked of other rows, since the old columns are not theirs. */
function askedAfresh(
  widget: ReportLayoutWidget,
  source: ReportSource | undefined,
): Partial<ReportLayoutWidget> {
  if (!source) return {};
  const presentation = source.presentations.includes(
    normalizePresentationKind(widget.presentation ?? "BAR_CHART"),
  )
    ? widget.presentation
    : source.presentations[0];
  return {
    source: source.name,
    presentation,
    spec: source.isSummarized
      ? specForQuestion({}, defaultQuestionFor(source, presentation), source)
      : {},
  };
}

export function createWidgetFromSource(
  source: ReportSource,
  id: string,
  /** How it is to be drawn, when that was settled first. */
  presentation?: PresentationKind,
): ReportLayoutWidget {
  const drawnAs = presentation ?? source.presentations[0];
  return {
    id,
    source: source.name,
    presentation: drawnAs,
    // A source that works its own figure out takes no question; one that is
    // summarized draws nothing at all until it has been asked something - and
    // what it is asked has to be something the drawing can answer.
    spec: source.isSummarized
      ? specForQuestion({}, defaultQuestionFor(source, drawnAs), source)
      : {},
    // What it is asking is what it is called.
    title: sentenceFor(defaultQuestionFor(source, drawnAs), source),
    entityScope: { mode: "layout" },
  };
}

export function createWidgetFromPresentation(
  presentation: PresentationKind,
  id: string,
): ReportLayoutWidget {
  return {
    id,
    presentation: normalizePresentationKind(presentation),
    spec: {},
    entityScope: { mode: "layout" },
  };
}

export const SOURCE_DRAG_PREFIX = "source:";
export const PRESENTATION_DRAG_PREFIX = "presentation:";

export function isSourceDragId(id: string) {
  return id.startsWith(SOURCE_DRAG_PREFIX);
}

export function isPresentationDragId(id: string) {
  return id.startsWith(PRESENTATION_DRAG_PREFIX);
}

export function sourceNameFromDragId(id: string): string {
  return id.slice(SOURCE_DRAG_PREFIX.length);
}

export function presentationEntryFromDragId(
  id: string,
): PresentationCatalogEntry | undefined {
  const entryId = id.slice(PRESENTATION_DRAG_PREFIX.length);
  return PRESENTATION_CATALOG.find((entry) => entry.id === entryId);
}

type EntityScopeMode = "layout" | "subset" | "single";

type ReportLayoutFilter = {
  key: string;
  type: string;
  operator: string;
  value: unknown;
  isLocked?: boolean;
};

type EntityScope = {
  mode: EntityScopeMode;
  entityIdentifiers?: string[];
};

export type ReportLayoutWidget = {
  id: string;
  /** What it is of: a service to summarize, or a worked-out source. */
  source?: string;
  /** How it is drawn. */
  presentation?: PresentationKind;
  /** The question itself: what to group, what to work out, what to narrow to. */
  spec?: Record<string, unknown>;
  title?: string;
  defaultFilters?: ReportLayoutFilter[];
  entityScope?: EntityScope;
  /** When set, the widget only appears in this layout group. */
  groupId?: string;
};

export type DashboardGridCard = {
  cardId: string;
  colSpan: number;
  visible: boolean;
  /** 1-based grid line; end exclusive. Used at md+ container widths. */
  colStart?: number;
  colEnd?: number;
  rowStart?: number;
  rowEnd?: number;
};

export type BreakpointLayouts = Record<
  BreakpointKey,
  { cards: DashboardGridCard[] }
>;

/** Shared layout group (tab) within a saved report layout definition. */
type ReportLayoutGroup = {
  id: string;
  name: string;
  layouts: BreakpointLayouts;
};

const CURRENT_DEFINITION_SCHEMA_VERSION = 3;

export type ReportLayoutDefinition = {
  version: number;
  /** Per-group grid layouts; canonical on the backend. */
  groups?: ReportLayoutGroup[];
  layouts: BreakpointLayouts;
  widgets: ReportLayoutWidget[];
};

export type CashflowAggregationMode = "combined" | "per_entity";

export type CashflowChartInput = {
  entityId: string;
  entityName: string;
  chart: ChartData;
};

function netValue(point: ChartData["points"][number] | undefined): number {
  if (!point) return 0;
  const raw = point.values.find((v) => v.key === "Net")?.value;
  return raw != null ? Number(raw) : 0;
}

export function mergeCashflowCharts(
  inputs: CashflowChartInput[],
  mode: CashflowAggregationMode,
): ChartData | null {
  const valid = inputs.filter((item) => item.chart?.points?.length);
  if (!valid.length) return null;

  const template = valid[0].chart;
  const labels = template.points.map((point) => point.label);

  if (mode === "per_entity") {
    const series = valid.map((item) => item.entityName);
    const points = labels.map((label, index) => ({
      __typename: "ChartPoint" as const,
      label,
      values: valid.map((item) => ({
        __typename: "ChartSeriesPoint" as const,
        key: item.entityName,
        value: netValue(item.chart.points[index]),
      })),
    }));

    return {
      ...template,
      title: "Cash flow by entity",
      series,
      points,
    };
  }

  const points = labels.map((label, index) => ({
    __typename: "ChartPoint" as const,
    label,
    values: [
      {
        __typename: "ChartSeriesPoint" as const,
        key: "Net",
        value: valid.reduce(
          (sum, item) => sum + netValue(item.chart.points[index]),
          0,
        ),
      },
    ],
  }));

  return {
    ...template,
    title: "Total cash flow",
    series: ["Net"],
    points,
  };
}

/** Grid columns per dashboard container breakpoint (min 2, max 6). */
export const BREAKPOINT_COLUMNS: Record<BreakpointKey, number> = {
  xs: 2,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 6,
};

type WidgetColSpanConstraints = {
  minColSpan: number;
  maxColSpan: number;
  defaultColSpan: number;
};

function clampSpan(span: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, span));
}

/** Every widget may be as wide as the grid; half of it is a sensible start. */
export function getWidgetColSpanConstraints(
  _widget: ReportLayoutWidget | undefined,
  columns: number,
): WidgetColSpanConstraints {
  return {
    minColSpan: 1,
    maxColSpan: columns,
    defaultColSpan: clampSpan(Math.ceil(columns / 2), 1, columns),
  };
}

export function clampCardColSpan(
  colSpan: number,
  _widget: ReportLayoutWidget | undefined,
  columns: number,
): number {
  return clampSpan(colSpan > 0 ? colSpan : 1, 1, columns);
}

/**
 * Lay out cards in order so each row fills up to `columns`, respecting per-widget
 * min/max spans. Stored `colSpan` is treated as the preferred width.
 */
/** Move a placeholder card to match catalog drag position over the canvas grid. */
export function reorderGhostCard(
  cards: DashboardGridCard[],
  ghostId: string,
  overId: string | null,
  ghostColSpan: number,
  canvasDropId: string,
): DashboardGridCard[] {
  const ghost = cards.find((c) => c.cardId === ghostId) ?? {
    cardId: ghostId,
    colSpan: ghostColSpan,
    visible: true,
  };
  const without = cards.filter((c) => c.cardId !== ghostId);
  const nextGhost = { ...ghost, colSpan: ghostColSpan, visible: true };

  if (!overId || overId === canvasDropId) {
    return [...without, nextGhost];
  }

  const overIndex = without.findIndex((c) => c.cardId === overId);
  if (overIndex === -1) {
    return [...without, nextGhost];
  }

  const next = [...without];
  next.splice(overIndex, 0, nextGhost);
  return next;
}

export function widgetUsesFlushContent(widget: ReportLayoutWidget): boolean {
  const presentation = widget.presentation
    ? normalizePresentationKind(widget.presentation)
    : "BAR_CHART";
  return (
    presentation === "TREE" ||
    presentation === "TABLE" ||
    presentation === "GROUPED_SUMMARY" ||
    presentation === "MATRIX"
  );
}

/** Layout-level filters in the report subnav — not shown on individual widgets. */
export const GLOBAL_OVERVIEW_FILTER_KEYS = new Set([
  "dates",
  "frequency",
  "entities",
]);

function pruneWidgetDefaultFilters(
  widget: ReportLayoutWidget,
): ReportLayoutWidget {
  return widget;
}

function getWidgetLocalFilterKeys(
  filterDefinitions: Record<string, FilterDefinitionType>,
): string[] {
  return Object.keys(filterDefinitions).filter(
    (key) => !GLOBAL_OVERVIEW_FILTER_KEYS.has(key),
  );
}

function sharedTypeToFilterType(type: string): FilterType["type"] {
  switch (type.toUpperCase()) {
    case "DATE":
      return "date";
    case "ENUM":
      return "enum";
    case "NUMBER":
      return "number";
    case "TEXT":
      return "text";
    default:
      return "text";
  }
}

function reportLayoutFilterToFilterType(
  filter: ReportLayoutFilter,
): FilterType {
  return {
    key: filter.key,
    type: sharedTypeToFilterType(filter.type),
    operator: normalizeFilterOperator(
      filter.operator as FilterType["operator"],
    ),
    value: filter.value,
    ...(filter.isLocked ? { isLocked: true } : {}),
  } as FilterType;
}

function filterTypeToReportLayoutFilter(
  filter: FilterType,
): ReportLayoutFilter | null {
  const operator = normalizeFilterOperator(filter.operator);
  if (!operator) {
    return null;
  }
  return {
    key: filter.key,
    type: String(filter.type).toUpperCase(),
    operator,
    value: filter.value,
    isLocked: filter.isLocked === true,
  };
}

/** ReportsViewWidget-local filters persisted on the widget (no injected defaults). */
function storedWidgetLocalFilters(
  widget: ReportLayoutWidget,
  localKeys: Set<string>,
): ReportLayoutFilter[] {
  return (widget.defaultFilters ?? []).filter((filter) =>
    localKeys.has(filter.key),
  );
}

/** Resolved widget-local filters (stored values with construct defaults for queries). */
function resolveWidgetLocalFilters(
  widget: ReportLayoutWidget,
  localKeys: string[],
): ReportLayoutFilter[] {
  const stored = new Map(
    storedWidgetLocalFilters(widget, new Set(localKeys)).map((filter) => [
      filter.key,
      filter,
    ]),
  );

  return localKeys
    .map((key) => stored.get(key))
    .filter((filter): filter is ReportLayoutFilter => filter != null);
}

export function widgetLocalFiltersFromWidget(
  widget: ReportLayoutWidget,
  filterDefinitions: Record<string, FilterDefinitionType>,
): FilterType[] {
  const localKeys = getWidgetLocalFilterKeys(filterDefinitions);
  return storedWidgetLocalFilters(widget, new Set(localKeys)).map(
    reportLayoutFilterToFilterType,
  );
}

export function buildWidgetHeaderFilterProps(
  widget: ReportLayoutWidget,
  filterDefinitions: Record<string, FilterDefinitionType>,
  onFiltersChange: (filters: FilterType[]) => void,
): {
  filters: FilterType[];
  filterDefinitions: Record<string, FilterDefinitionType>;
  onFiltersChange: (filters: FilterType[]) => void;
} | null {
  if (getWidgetPairingStatus(widget) !== "complete") {
    return null;
  }

  const localFilterDefinitions = Object.fromEntries(
    Object.entries(filterDefinitions).filter(
      ([key]) => !GLOBAL_OVERVIEW_FILTER_KEYS.has(key),
    ),
  );
  if (Object.keys(localFilterDefinitions).length === 0) {
    return null;
  }

  return {
    filters: widgetLocalFiltersFromWidget(widget, filterDefinitions),
    filterDefinitions: localFilterDefinitions,
    onFiltersChange,
  };
}

export function mergeWidgetDefaultFilters(
  widget: ReportLayoutWidget,
  localFilters: FilterType[],
  filterDefinitions: Record<string, FilterDefinitionType>,
): ReportLayoutFilter[] {
  const localKeys = new Set(getWidgetLocalFilterKeys(filterDefinitions));
  const preserved = (widget.defaultFilters ?? []).filter(
    (filter) => !localKeys.has(filter.key),
  );
  const persistedLocal = localFilters
    .map(filterTypeToReportLayoutFilter)
    .filter((filter): filter is ReportLayoutFilter => filter != null);
  return [...preserved, ...persistedLocal];
}

function mergeReportLayoutFilters(
  ...layers: ReportLayoutFilter[][]
): ReportLayoutFilter[] {
  const map = new Map<string, ReportLayoutFilter>();
  for (const layer of layers) {
    for (const filter of layer) {
      if (filter.isLocked && map.has(filter.key)) continue;
      map.set(filter.key, filter);
    }
  }
  return [...map.values()];
}

/** Global report filters from layout subnav plus widget-local filters from the card. */
export function resolveEffectiveWidgetFilters(
  widget: ReportLayoutWidget,
  globalFilters: FilterType[],
  filterDefinitions: Record<string, FilterDefinitionType>,
  localFiltersOverride?: FilterType[],
): ReportLayoutFilter[] {
  const localKeys = getWidgetLocalFilterKeys(filterDefinitions);
  const localKeysSet = new Set(localKeys);
  const local =
    localFiltersOverride != null
      ? localFiltersOverride
          .map(filterTypeToReportLayoutFilter)
          .filter((filter): filter is ReportLayoutFilter => filter != null)
          .filter((filter) => localKeysSet.has(filter.key))
      : resolveWidgetLocalFilters(widget, localKeys);

  const effectiveGlobal = getReportsFilters(globalFilters)
    .filter((filter) => GLOBAL_OVERVIEW_FILTER_KEYS.has(filter.key))
    .map(filterTypeToReportLayoutFilter);

  return mergeReportLayoutFilters(effectiveGlobal, local);
}

type CashflowViewerFilters = {
  startDate: string;
  endDate: string;
  frequency: FrequencyEnum;
  entityIds: string[];
  aggregationMode: CashflowAggregationMode;
  mode: MetricModeEnum;
};

function toDateInputValue(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

export function buildTransactionFilterFromWidgetFilters(
  filters: ReportLayoutFilter[],
): Record<string, unknown> | undefined {
  const branches: Record<string, unknown>[] = [];
  const fmap = new Map(filters.map((filter) => [filter.key, filter]));

  const effectiveDate = fmap.get("effective_date");
  if (
    effectiveDate?.operator === "range" &&
    effectiveDate.value &&
    typeof effectiveDate.value === "object"
  ) {
    const range = effectiveDate.value as { date1?: string; date2?: string };
    if (range.date1) {
      branches.push({ field: { effectiveDate: { gte: range.date1 } } });
    }
    if (range.date2) {
      branches.push({ field: { effectiveDate: { lte: range.date2 } } });
    }
  }

  const amount = fmap.get("amount");
  if (amount?.value != null && amount.operator) {
    const operator = amount.operator === "equals" ? "exact" : amount.operator;
    branches.push({ field: { amount: { [operator]: amount.value } } });
  }

  if (branches.length === 0) return undefined;
  if (branches.length === 1) return branches[0];
  return { all: branches };
}

export function cashflowFiltersToQueryVariables(
  filters: CashflowViewerFilters,
) {
  const startDate = filters.startDate
    ? new Date(`${filters.startDate}T00:00:00`).toISOString()
    : undefined;
  const endDate = filters.endDate
    ? new Date(`${filters.endDate}T23:59:59`).toISOString()
    : undefined;

  return {
    startDate,
    endDate,
    frequency: filters.frequency,
    entityIds: filters.entityIds,
    aggregationMode: filters.aggregationMode,
    mode: filters.mode,
  };
}

export const BREAKPOINTS: BreakpointKey[] = ["xs", "sm", "md", "lg", "xl"];

const REMOVED_WIDGET_IDS = new Set(["onboarding-checklist"]);

function defaultReportFilters(): ReportLayoutFilter[] {
  const year = new Date().getFullYear();
  return [
    {
      key: "dates",
      type: "DATE",
      operator: "range",
      value: {
        date1: `${year}-01-01T00:00:00+00:00`,
        date2: `${year}-12-31T23:59:59+00:00`,
      },
      isLocked: false,
    },
    {
      key: "frequency",
      type: "ENUM",
      operator: "exact",
      value: "MONTHLY",
      isLocked: false,
    },
  ];
}

function defaultLayoutForBreakpoint(
  breakpoint: BreakpointKey,
  widgets: ReportLayoutWidget[],
): ReportLayoutDefinition["layouts"][BreakpointKey] {
  const mode = isDesktopLayoutBreakpoint(breakpoint) ? "grid" : "stack";
  const columns = mode === "grid" ? REPORTS_DESKTOP_COLUMNS : 1;
  const cards = widgets.map((widget) => {
    // The default report's own spans fill each row; a widget added later
    // takes its construct's default.
    const dashboardCard = DASHBOARD_CARDS.find((card) => card.id === widget.id);
    return {
      cardId: widget.id,
      colSpan:
        dashboardCard?.defaultColSpan ??
        getWidgetColSpanConstraints(widget, columns).defaultColSpan,
      visible: true,
    };
  });
  const widgetsById = new Map(widgets.map((w) => [w.id, w]));
  return { cards: fitCardsToGrid(cards, widgetsById, mode) };
}

/**
 * What each card of the default report is of, and what it asks.
 *
 * Spending by category was a source of its own until the database could be
 * asked for the transactions grouped by what they were for: the same answer,
 * through the path every other summarized widget takes. It asks for what left
 * the bank accounts - not money in, nor the lines of an entry beside the
 * bank's - as the server's own default does (``SPENDING_BY_CATEGORY``).
 */
const DEFAULT_CARD_WIDGETS: Record<
  string,
  {
    source: string;
    presentation: PresentationKind;
    spec?: Record<string, unknown>;
  }
> = {
  "overview-kpis": { source: "overview_kpis", presentation: "KPI_STRIP" },
  "money-in-out-chart": { source: "money_in_out", presentation: "BAR_CHART" },
  "spending-chart": {
    source: "transactions",
    presentation: "PIE_CHART",
    spec: {
      groupBy: [{ field: "category_obj__name" }],
      aggregate: [{ op: "sum", field: "amount" }],
      where: {
        connector: "all",
        terms: [
          { field: "amount", operator: "lt", value: 0 },
          {
            field: "financial_account__name",
            operator: "is_null",
            value: true,
            isNegated: true,
          },
        ],
      },
    },
  },
  "bank-accounts": { source: "bank_accounts", presentation: "GROUPED_SUMMARY" },
};

function defaultWidgetFromCard(
  card: (typeof DASHBOARD_CARDS)[number],
): ReportLayoutWidget {
  const wanted = DEFAULT_CARD_WIDGETS[card.id] ?? {
    source: "money_in_out",
    presentation: "BAR_CHART" as PresentationKind,
  };
  return {
    id: card.id,
    source: wanted.source,
    presentation: wanted.presentation,
    spec: wanted.spec ?? {},
    title: card.label,
    defaultFilters: defaultReportFilters(),
    entityScope: { mode: "layout" },
  };
}

function defaultReportDefinition(): ReportLayoutDefinition {
  const widgets: ReportLayoutWidget[] = DASHBOARD_CARDS.map(
    defaultWidgetFromCard,
  );

  const layouts = Object.fromEntries(
    BREAKPOINTS.map((bp) => [bp, defaultLayoutForBreakpoint(bp, widgets)]),
  ) as ReportLayoutDefinition["layouts"];

  const groups: ReportLayoutGroup[] = [
    { id: "default", name: "Tab 1", layouts },
  ];
  return {
    version: CURRENT_DEFINITION_SCHEMA_VERSION,
    groups,
    layouts,
    widgets,
  };
}

const DEFAULT_GROUP_ID = "default";

/** Widgets without ``groupId`` belong to the primary group only (legacy). */
function widgetBelongsToGroup(
  widget: ReportLayoutWidget,
  groupId: string,
  primaryGroupId: string,
): boolean {
  if (!widget.groupId) return groupId === primaryGroupId;
  return widget.groupId === groupId;
}

function emptyGroupLayouts(): BreakpointLayouts {
  return Object.fromEntries(
    BREAKPOINTS.map((bp) => [bp, { cards: [] }]),
  ) as BreakpointLayouts;
}

/** Return layout groups, migrating legacy ``tabs`` / root-only layouts when needed. */
export function getReportGroups(
  definition: ReportLayoutDefinition,
): ReportLayoutGroup[] {
  const normalized = normalizeReportDefinition(definition);
  return normalized.groups ?? [];
}

export function resolveActiveReportGroupId(
  definition: ReportLayoutDefinition,
  activeGroupId: string | null | undefined,
): string {
  const groups = getReportGroups(definition);
  if (!groups.length) return DEFAULT_GROUP_ID;
  if (activeGroupId && groups.some((g) => g.id === activeGroupId)) {
    return activeGroupId;
  }
  return groups[0].id;
}

/** Layout grids for the active group (mirrors group entry on root ``layouts``). */
export function definitionForActiveReportGroup(
  definition: ReportLayoutDefinition,
  activeGroupId: string | null | undefined,
): ReportLayoutDefinition {
  const normalized = normalizeReportDefinition(definition);
  const groupId = resolveActiveReportGroupId(normalized, activeGroupId);
  const group = normalized.groups?.find((g) => g.id === groupId);
  if (!group) return normalized;
  const primaryGroupId = normalized.groups?.[0]?.id ?? DEFAULT_GROUP_ID;
  const widgets = normalized.widgets.filter((w) =>
    widgetBelongsToGroup(w, groupId, primaryGroupId),
  );
  return { ...normalized, layouts: group.layouts, widgets };
}

/** Merge a tab-scoped edit back into the full multi-group definition. */
export function mergeActiveGroupIntoDefinition(
  full: ReportLayoutDefinition,
  tabScoped: ReportLayoutDefinition,
  activeGroupId: string | null | undefined,
): ReportLayoutDefinition {
  const normalized = normalizeReportDefinition(full);
  if (!normalized.groups?.length) {
    return normalizeReportDefinition(tabScoped);
  }

  const tabId = resolveActiveReportGroupId(normalized, activeGroupId);
  const primaryGroupId = normalized.groups[0]?.id ?? DEFAULT_GROUP_ID;
  const rawGroupLayouts = tabScoped.groups?.find(
    (group) => group.id === tabId,
  )?.layouts;
  const nextTabScoped = normalizeReportDefinition(tabScoped);
  const normalizedGroupLayouts = nextTabScoped.groups?.find(
    (group) => group.id === tabId,
  )?.layouts;
  // Normalize rebuilds root layouts from the primary group and can erase tab-scoped
  // grid edits that only exist on ``tabScoped.layouts`` — read raw layouts first.
  const activeGroupLayouts =
    tabId === primaryGroupId
      ? (tabScoped.layouts ??
        rawGroupLayouts ??
        normalizedGroupLayouts ??
        nextTabScoped.layouts)
      : (rawGroupLayouts ??
        normalizedGroupLayouts ??
        tabScoped.layouts ??
        nextTabScoped.layouts);
  // Replace every widget visible on the active tab (including legacy widgets
  // without groupId on the primary tab) with the tab-scoped edit — otherwise
  // removals are undone.
  const widgets = [
    ...new Map(
      [
        ...normalized.widgets.filter(
          (widget) => !widgetBelongsToGroup(widget, tabId, primaryGroupId),
        ),
        ...nextTabScoped.widgets,
      ].map((widget) => [widget.id, widget]),
    ).values(),
  ];

  return normalizeReportDefinition({
    ...normalized,
    widgets,
    // Keep root layouts in sync when editing the primary tab so normalize does
    // not merge stale grid coords from the previous root snapshot.
    ...(tabId === primaryGroupId ? { layouts: activeGroupLayouts } : {}),
    groups: normalized.groups.map((group) =>
      group.id === tabId ? { ...group, layouts: activeGroupLayouts } : group,
    ),
  });
}

export function reorderReportGroupsInDefinition(
  definition: ReportLayoutDefinition,
  orderedGroupIds: string[],
): ReportLayoutDefinition {
  const normalized = normalizeReportDefinition(definition);
  const groups = normalized.groups ?? [];
  const byId = new Map(groups.map((group) => [group.id, group]));
  const next: ReportLayoutGroup[] = [];
  for (const id of orderedGroupIds) {
    const group = byId.get(id);
    if (group) {
      next.push(group);
      byId.delete(id);
    }
  }
  for (const group of byId.values()) {
    next.push(group);
  }
  if (next.length !== groups.length) return normalized;
  const unchanged = next.every(
    (group, index) => group.id === groups[index]?.id,
  );
  if (unchanged) return normalized;
  return normalizeReportDefinition({ ...normalized, groups: next });
}

export function renameReportGroupInDefinition(
  definition: ReportLayoutDefinition,
  groupId: string,
  name: string,
): ReportLayoutDefinition {
  const normalized = normalizeReportDefinition(definition);
  const trimmed = name.trim();
  if (!trimmed) return normalized;
  const groups = (normalized.groups ?? []).map((group) =>
    group.id === groupId ? { ...group, name: trimmed } : group,
  );
  const previous = normalized.groups?.find((group) => group.id === groupId);
  if (!previous || previous.name === trimmed) return normalized;
  return normalizeReportDefinition({ ...normalized, groups });
}

export function removeReportGroupFromDefinition(
  definition: ReportLayoutDefinition,
  groupId: string,
): ReportLayoutDefinition | null {
  const normalized = normalizeReportDefinition(definition);
  const groups = normalized.groups ?? [];
  if (groups.length <= 1) return null;
  const nextGroups = groups.filter((group) => group.id !== groupId);
  if (nextGroups.length === groups.length) return null;
  const primaryLayouts = nextGroups[0]?.layouts ?? normalized.layouts;
  const widgets = (normalized.widgets ?? []).filter(
    (widget) => widget.groupId !== groupId,
  );
  return normalizeReportDefinition({
    ...normalized,
    groups: nextGroups,
    layouts: primaryLayouts,
    widgets,
  });
}

export function addReportGroupToDefinition(
  definition: ReportLayoutDefinition,
  name?: string,
): { definition: ReportLayoutDefinition; newGroupId: string } {
  const normalized = normalizeReportDefinition(definition);
  const groups = normalized.groups ?? [];
  const newGroupId = `group-${Date.now()}`;
  const newGroup: ReportLayoutGroup = {
    id: newGroupId,
    name: name ?? `Tab ${groups.length + 1}`,
    layouts: emptyGroupLayouts(),
  };
  const nextGroups = [...groups, newGroup];
  return {
    definition: normalizeReportDefinition({
      ...normalized,
      groups: nextGroups,
      layouts: newGroup.layouts,
    }),
    newGroupId,
  };
}

export function updateActiveReportGroupLayouts(
  definition: ReportLayoutDefinition,
  activeGroupId: string | null | undefined,
  breakpoint: BreakpointKey,
  cards: ReportLayoutDefinition["layouts"][BreakpointKey]["cards"],
): ReportLayoutDefinition {
  const normalized = normalizeReportDefinition(definition);
  const groupId = resolveActiveReportGroupId(normalized, activeGroupId);
  const groups = (normalized.groups ?? []).map((group) =>
    group.id === groupId
      ? {
          ...group,
          layouts: {
            ...group.layouts,
            [breakpoint]: { cards },
          },
        }
      : group,
  );
  const activeLayouts =
    groups.find((g) => g.id === groupId)?.layouts ?? normalized.layouts;
  return normalizeReportDefinition({
    ...normalized,
    groups,
    layouts: activeLayouts,
  });
}

/** Ensure widgets exist and layout cards reference valid widget ids. */
export function normalizeReportDefinition(
  raw: unknown,
): ReportLayoutDefinition {
  const base = defaultReportDefinition();
  if (!raw || typeof raw !== "object") return base;

  const input = raw as Partial<ReportLayoutDefinition> & {
    tabs?: ReportLayoutGroup[];
  };
  let widgets = Array.isArray(input.widgets) ? [...input.widgets] : [];
  widgets = widgets
    .filter((widget) => {
      if (
        widget.source === "overview_kpis" &&
        widget.id === "onboarding-checklist"
      ) {
        return false;
      }
      if (REMOVED_WIDGET_IDS.has(widget.id)) {
        return false;
      }
      return true;
    })
    .map((widget) => {
      const normalized = pruneWidgetDefaultFilters(normalizeWidget(widget));
      const rest = { ...normalized } as ReportLayoutWidget & {
        type?: unknown;
        config?: unknown;
      };
      delete rest.type;
      delete rest.config;
      return rest;
    });

  // Tab merges can briefly carry the same widget id twice; keep the last entry.
  widgets = [...new Map(widgets.map((widget) => [widget.id, widget])).values()];

  if (widgets.length === 0) {
    const layouts = input.layouts ?? base.layouts;
    const cardIds = new Set<string>();
    for (const bp of BREAKPOINTS) {
      for (const card of layouts[bp]?.cards ?? []) {
        if (!REMOVED_WIDGET_IDS.has(card.cardId)) {
          cardIds.add(card.cardId);
        }
      }
    }
    widgets = [...cardIds].map((id) => {
      const card = DASHBOARD_CARDS.find((c) => c.id === id);
      if (card) return defaultWidgetFromCard(card);
      return {
        id,
        source: "cash_flow",
        presentation: "BAR_CHART",
        spec: {},
        entityScope: { mode: "layout" },
      };
    });
  }

  const widgetIds = new Set(widgets.map((w) => w.id));
  const widgetsById = new Map(widgets.map((w) => [w.id, w]));
  const layouts = { ...base.layouts, ...(input.layouts ?? {}) };

  for (const bp of BREAKPOINTS) {
    const columns = isDesktopLayoutBreakpoint(bp)
      ? REPORTS_DESKTOP_COLUMNS
      : BREAKPOINT_COLUMNS[bp];
    const existing = layouts[bp]?.cards ?? [];
    const seen = new Set<string>();
    const cards = existing
      .filter(
        (c) =>
          widgetIds.has(c.cardId) &&
          !seen.has(c.cardId) &&
          !REMOVED_WIDGET_IDS.has(c.cardId),
      )
      .map((c) => {
        seen.add(c.cardId);
        const widget = widgetsById.get(c.cardId);
        return {
          ...c,
          colSpan: clampCardColSpan(c.colSpan, widget, columns),
        };
      });

    for (const widget of widgets) {
      if (!seen.has(widget.id)) {
        const { defaultColSpan } = getWidgetColSpanConstraints(widget, columns);
        cards.push({
          cardId: widget.id,
          colSpan: defaultColSpan,
          visible: true,
        });
      }
    }

    layouts[bp] = {
      cards: isDesktopLayoutBreakpoint(bp)
        ? ensureGridPlacement(cards, widgetsById, columns)
        : cards,
    };
  }

  const rawGroups = Array.isArray(input.groups)
    ? input.groups
    : Array.isArray(input.tabs)
      ? input.tabs
      : undefined;

  let groups: ReportLayoutGroup[] | undefined = rawGroups
    ?.filter(
      (group): group is ReportLayoutGroup =>
        Boolean(group) &&
        typeof group === "object" &&
        typeof (group as ReportLayoutGroup).id === "string" &&
        typeof (group as ReportLayoutGroup).name === "string",
    )
    .map((group) => ({
      id: group.id,
      name: group.name,
      layouts: { ...base.layouts, ...(group.layouts ?? {}) },
    }));

  if (!groups?.length) {
    groups = [
      {
        id: DEFAULT_GROUP_ID,
        name: "Tab 1",
        layouts,
      },
    ];
  }

  const desktopRootCards = resolveDesktopLayoutCards(layouts);
  const primaryGroupId = groups[0]?.id ?? DEFAULT_GROUP_ID;

  for (const bp of BREAKPOINTS) {
    const columns = isDesktopLayoutBreakpoint(bp)
      ? REPORTS_DESKTOP_COLUMNS
      : BREAKPOINT_COLUMNS[bp];
    for (const group of groups) {
      const isPrimaryGroup = group.id === primaryGroupId;
      const existing = isPrimaryGroup
        ? (layouts[bp]?.cards ?? group.layouts[bp]?.cards ?? [])
        : (group.layouts[bp]?.cards ?? []);
      const seen = new Set<string>();
      const cards = existing
        .filter((c) => {
          if (!widgetIds.has(c.cardId) || seen.has(c.cardId)) return false;
          const widget = widgets.find((w) => w.id === c.cardId);
          return widget
            ? widgetBelongsToGroup(widget, group.id, primaryGroupId)
            : false;
        })
        .map((c) => {
          seen.add(c.cardId);
          const widget = widgetsById.get(c.cardId);
          const rootCard = desktopRootCards.find(
            (card) => card.cardId === c.cardId,
          );
          const merged = isPrimaryGroup
            ? c
            : mergeCardGridPlacement(c, rootCard);
          return {
            ...merged,
            colSpan: clampCardColSpan(merged.colSpan, widget, columns),
          };
        });
      for (const widget of widgets) {
        if (
          !seen.has(widget.id) &&
          widgetBelongsToGroup(widget, group.id, primaryGroupId)
        ) {
          const { defaultColSpan } = getWidgetColSpanConstraints(
            widget,
            columns,
          );
          cards.push({
            cardId: widget.id,
            colSpan: defaultColSpan,
            visible: true,
          });
        }
      }
      group.layouts[bp] = {
        cards: isDesktopLayoutBreakpoint(bp)
          ? ensureGridPlacement(cards, widgetsById, columns)
          : cards,
      };
    }
  }

  for (const group of groups) {
    group.layouts = canonicalizeStackReportLayouts(
      canonicalizeReportLayouts(group.layouts, widgetsById),
    );
  }

  const primaryLayouts = canonicalizeStackReportLayouts(
    canonicalizeReportLayouts(
      groups.find((g) => g.id === primaryGroupId)?.layouts ?? layouts,
      widgetsById,
    ),
  );

  return {
    version: CURRENT_DEFINITION_SCHEMA_VERSION,
    groups,
    layouts: primaryLayouts,
    widgets,
  };
}

export function definitionToProfile(
  layoutId: string,
  layoutName: string,
  definition: ReportLayoutDefinition,
): DashboardProfile {
  return {
    id: layoutId,
    name: layoutName,
    layouts: definition.layouts,
  };
}

export function addWidgetToDefinition(
  definition: ReportLayoutDefinition,
  widget: ReportLayoutWidget,
  colSpan?: number,
  activeGroupId?: string | null,
): ReportLayoutDefinition {
  const groupId =
    activeGroupId != null
      ? resolveActiveReportGroupId(definition, activeGroupId)
      : undefined;
  const widgetWithGroup: ReportLayoutWidget = {
    ...widget,
    ...(groupId ? { groupId } : {}),
  };
  const next = normalizeReportDefinition({
    ...definition,
    widgets: [...definition.widgets, widgetWithGroup],
  });
  if (colSpan == null) return next;

  const primaryGroupId = next.groups?.[0]?.id ?? DEFAULT_GROUP_ID;
  for (const bp of BREAKPOINTS) {
    const columns = BREAKPOINT_COLUMNS[bp];
    const preferred = clampCardColSpan(colSpan, widgetWithGroup, columns);
    const patchCards = (cards: (typeof next.layouts)[typeof bp]["cards"]) =>
      cards.map((c) =>
        c.cardId === widget.id ? { ...c, colSpan: preferred } : c,
      );

    next.layouts[bp] = { cards: patchCards(next.layouts[bp].cards) };
    for (const group of next.groups ?? []) {
      if (!widgetBelongsToGroup(widgetWithGroup, group.id, primaryGroupId))
        continue;
      group.layouts[bp] = {
        cards: patchCards(group.layouts[bp]?.cards ?? []),
      };
    }
  }
  return next;
}

export function removeWidgetFromDefinition(
  definition: ReportLayoutDefinition,
  widgetId: string,
): ReportLayoutDefinition {
  const stripLayouts = (layouts: BreakpointLayouts): BreakpointLayouts =>
    Object.fromEntries(
      BREAKPOINTS.map((bp) => [
        bp,
        {
          cards: (layouts[bp]?.cards ?? []).filter(
            (c) => c.cardId !== widgetId,
          ),
        },
      ]),
    ) as BreakpointLayouts;

  return normalizeReportDefinition({
    ...definition,
    widgets: definition.widgets.filter((w) => w.id !== widgetId),
    layouts: stripLayouts(definition.layouts),
    groups: definition.groups?.map((group) => ({
      ...group,
      layouts: stripLayouts(group.layouts),
    })),
  });
}

export function updateWidgetInDefinition(
  definition: ReportLayoutDefinition,
  widgetId: string,
  patch: Partial<ReportLayoutWidget>,
): ReportLayoutDefinition {
  return {
    ...definition,
    widgets: definition.widgets.map((w) =>
      w.id === widgetId ? { ...w, ...patch, id: w.id } : w,
    ),
  };
}

export function resolveWidgetEntityIdentifiers(
  widget: ReportLayoutWidget,
  layoutEntityIdentifiers: string[],
): string[] {
  const published = layoutEntityIdentifiers.map(String);
  const scope: EntityScope = widget.entityScope ?? { mode: "layout" };

  if (scope.mode === "layout") {
    return published;
  }
  if (scope.mode === "single") {
    const ids = scope.entityIdentifiers ?? [];
    if (ids.length) {
      const eid = String(ids[0]);
      return published.includes(eid) ? [eid] : published.slice(0, 1);
    }
    return published.slice(0, 1);
  }
  const ids = (scope.entityIdentifiers ?? [])
    .map(String)
    .filter((eid) => published.includes(eid));
  return ids.length ? ids : published;
}

function ReportsViewDraggableCatalogItem({
  id,
  label,
  description,
  asks,
  ReactIcon,
  isPrivate,
  dragDisabled,
  onAdd,
}: {
  id: string;
  label: string;
  description: string;
  /** Taken by a tap - or a click - to the end of the report, not dragged. */
  onAdd?: () => void;
  /** A question this source answers, under what it is. */
  asks?: string;
  /** What the rows are, in a mark: the same one they wear everywhere else. */
  ReactIcon?: LucideIcon;
  isPrivate: boolean;
  dragDisabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    disabled: dragDisabled,
    data: { type: "assemble", id },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      // A drag never ends in a click: dnd-kit swallows the one after it.
      onClick={dragDisabled ? undefined : onAdd}
      data-testid={`report-catalog-${id}`}
      // A tile in the catalogue it is picked from, recessed off the panel
      // behind it and with no border of its own - it takes on the raised
      // card surface once it lands on the canvas.
      className={cn(
        "flex items-start gap-1.5 pl-3 pr-3 pt-2 pb-2 rounded-lg bg-muted dark:bg-[#151719]",
        dragDisabled
          ? "cursor-not-allowed"
          : isDragging
            ? "cursor-grabbing"
            : "cursor-grab",
        isDragging
          ? "opacity-[0.45]"
          : dragDisabled
            ? "opacity-[0.5]"
            : "opacity-[1]",
        "[transition:background-color_0.1s_ease]",
        !dragDisabled && "hover:bg-accent dark:hover:bg-muted",
      )}
    >
      {/* What the rows are, and under it the hold to drag them by: one column
          at the head of the tile rather than two marks competing for the same
          line. */}
      <div className="flex flex-col items-center gap-1 pt-px shrink-0">
        {ReactIcon && (
          <div className="flex text-muted-foreground">
            <ReactIcon size={14} />
          </div>
        )}
        {/* A finger taps a tile in rather than dragging it: no hold to show. */}
        <div
          data-testid="report-catalog-grip"
          className="flex text-muted-foreground/80 pointer-coarse:hidden"
        >
          <GripVerticalIcon size={14} />
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 flex-wrap">
          <Typography level="title-sm">{label}</Typography>
          {isPrivate && <Badge variant="secondary">Private</Badge>}
        </div>
        {description && (
          <Typography level="body-xs" className="text-muted-foreground/80">
            {description}
          </Typography>
        )}
        {asks && (
          <Typography
            level="body-xs"
            className="text-muted-foreground/80 italic"
          >
            {asks}
          </Typography>
        )}
      </div>
    </div>
  );
}

export function ReportsViewCatalogSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-3">
      <Typography
        level="body-xs"
        className="text-muted-foreground/80 font-semibold uppercase [letter-spacing:0.05em] mb-1"
      >
        {title}
      </Typography>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

export function ReportsViewSourceCatalogItem({
  source,
  dragDisabled = false,
  onAdd,
}: {
  source: ReportSource;
  dragDisabled?: boolean;
  /** Taken by a tap - or a click - to the end of the report, not dragged. */
  onAdd?: (id: string) => void;
}) {
  // What the rows are and a question they answer, declared beside the rows
  // themselves in @/objects - "Grouped and totalled by the database" said how
  // a source works, which is not what somebody choosing one is asking.
  const object = objectForReportSource(source.name);
  return (
    <ReportsViewDraggableCatalogItem
      id={`${SOURCE_DRAG_PREFIX}${source.name}`}
      label={source.label}
      description={object?.report?.description ?? ""}
      asks={object?.report?.asks}
      ReactIcon={object?.ReactIcon}
      isPrivate={false}
      dragDisabled={dragDisabled}
      onAdd={onAdd && (() => onAdd(`${SOURCE_DRAG_PREFIX}${source.name}`))}
    />
  );
}

export function ReportsViewPresentationCatalogItem({
  entry,
}: {
  entry: PresentationCatalogEntry;
}) {
  return (
    <ReportsViewDraggableCatalogItem
      id={`${PRESENTATION_DRAG_PREFIX}${entry.id}`}
      label={entry.label}
      description={entry.description}
      isPrivate={false}
      dragDisabled={false}
    />
  );
}

type GraphQLDateRangeInput = {
  start: string;
  end: string;
};

function toIsoDateTime(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  return value;
}

function layoutDateRangeToGraphQLInput(
  value: unknown,
): GraphQLDateRangeInput | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const start = toIsoDateTime(record.date1 ?? record.start);
  const end = toIsoDateTime(record.date2 ?? record.end);
  if (!start || !end) return null;
  return { start, end };
}

function widgetTimeframeFiltersForGraphQL(widget: ReportLayoutWidget) {
  const datesFilter = widget.defaultFilters?.find(
    (filter) => filter.key === "dates",
  );
  const frequencyFilter = widget.defaultFilters?.find(
    (filter) => filter.key === "frequency",
  );
  const dates = datesFilter
    ? layoutDateRangeToGraphQLInput(datesFilter.value)
    : null;
  const frequency = frequencyFilter?.value;

  return {
    ...(dates
      ? {
          dates: {
            value: dates,
            isLocked: Boolean(datesFilter?.isLocked),
          },
        }
      : {}),
    ...(frequency
      ? {
          frequency: {
            value: String(frequency) as FrequencyEnum,
            isLocked: Boolean(frequencyFilter?.isLocked),
          },
        }
      : {}),
  };
}

function layoutCardsToGraphQLInput(
  layouts: ReportLayoutDefinition["layouts"],
): Record<string, { cards: Array<Record<string, unknown>> }> {
  const breakpoints = ["xs", "sm", "md", "lg", "xl"] as const;
  return Object.fromEntries(
    breakpoints.map((breakpoint) => [
      breakpoint,
      {
        cards: (layouts[breakpoint]?.cards ?? []).map((card) => ({
          cardId: card.cardId,
          colSpan: card.colSpan,
          visible: card.visible,
          ...(card.colStart != null ? { colStart: card.colStart } : {}),
          ...(card.colEnd != null ? { colEnd: card.colEnd } : {}),
          ...(card.rowStart != null ? { rowStart: card.rowStart } : {}),
          ...(card.rowEnd != null ? { rowEnd: card.rowEnd } : {}),
        })),
      },
    ]),
  );
}

function widgetToGraphQLInput(widget: ReportLayoutWidget) {
  const presentation = widget.presentation
    ? normalizePresentationKind(widget.presentation)
    : undefined;
  if (!widget.source || !presentation) {
    throw new Error("A widget must say what it is of and how it is drawn.");
  }

  const scope = widget.entityScope ?? { mode: "layout" as const };
  return {
    id: widget.id,
    source: widget.source,
    presentation,
    spec: widget.spec ?? {},
    ...(widget.title ? { title: widget.title } : {}),
    ...(widget.groupId ? { groupId: widget.groupId } : {}),
    entityScope: {
      mode: scope.mode.toUpperCase(),
      ...(scope.entityIdentifiers?.length
        ? { entityIdentifiers: scope.entityIdentifiers }
        : {}),
    },
    ...widgetTimeframeFiltersForGraphQL(widget),
  };
}

/** Convert editor definition JSON to typed GraphQL ``ReportDefinitionInput``. */
export function reportLayoutDefinitionToGraphQLInput(
  definition: ReportLayoutDefinition,
): Record<string, unknown> {
  return {
    version: definition.version ?? CURRENT_DEFINITION_SCHEMA_VERSION,
    groups: (definition.groups ?? []).map((group) => ({
      id: group.id,
      name: group.name,
      layouts: layoutCardsToGraphQLInput(group.layouts),
    })),
    layouts: layoutCardsToGraphQLInput(definition.layouts),
    widgets: definition.widgets.map(widgetToGraphQLInput),
  };
}
